import { memo, useRef, useCallback, useEffect, forwardRef } from 'react';
import { useInView } from 'react-intersection-observer';

import useCombinedRef from '@Hooks/useCombinedRef';
import styles from './Video.module.css';

import type { HTMLAttributes } from 'react';

export interface VideoProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  src: string;
  poster?: string;
  autoPlay?: boolean;
  controls?: boolean;
  stopOnScroll?: boolean;
  playOnHover?: boolean;
  loop?: boolean;
}

const Video = forwardRef<HTMLVideoElement, VideoProps>((props, videoRef) => {
  const {
    src,
    poster = null,
    autoPlay = false,
    controls = false,
    stopOnScroll = false,
    playOnHover = false,
    loop,
    ...restProps
  } = props;

  const video = useRef<HTMLVideoElement>(null);
  const videoRefs = useCombinedRef(video, videoRef);

  const playVideo = useCallback(() => {
    video.current.play().catch(() => {});
  }, []);

  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '100px 0px',
  });
  const [refVideo, inViewVideo] = useInView({
    rootMargin: '0px 0px',
  });

  const handleMouseEnter = useCallback(() => {
    if (!playOnHover) return null;
    return playVideo();
  }, [playOnHover, playVideo]);

  const handleMouseLeave = useCallback(() => {
    if (!playOnHover) return null;
    return video.current.pause();
  }, [playOnHover]);

  useEffect(() => {
    if (!video.current) return;

    if (stopOnScroll && !playOnHover) {
      if (inViewVideo) {
        playVideo();
      } else {
        video.current.pause();
      }
    }
  }, [playOnHover, inViewVideo, stopOnScroll, playVideo]);

  return (
    <div {...restProps} ref={ref}>
      {inView && (
        <div
          className={styles.helper}
          ref={refVideo}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <video
            className={styles.player}
            autoPlay={autoPlay}
            controls={controls}
            src={src}
            poster={poster}
            loop={loop}
            muted
            playsInline
            ref={videoRefs}
          >
            <source type='video/mp4' src={src} media='(orientation:landscape)' />
          </video>
        </div>
      )}
    </div>
  );
});

Video.displayName = 'Video';

export default memo(Video);
